/**
 * إعدادات متعددة المستويات: المنصة < الشركة < العلامة < الفرع < المستخدم.
 * الإعدادات هنا إعدادات تشغيل واجهة/سياسات، وليست أسرارًا أو مفاتيح.
 */

import type { DataGateway } from '@/data/types';
import type { CompanyModule, ModuleKey, SettingRecord, ThemeTokens } from '@/domain/types';
import { AppError } from '@/domain/errors';
import { newId } from '@/domain/ids';
import { AUDIT_ACTIONS } from '@/domain/audit';
import {
  DEFAULT_APPROVAL_POLICY,
  DEFAULT_COMPLETED_TASK_RETENTION,
  DEFAULT_MEDIA_HUB_POLICY,
  DEFAULT_NOTIFICATION_POLICY,
  DEFAULT_OPERATIONAL_FEATURES,
  DEFAULT_PERSONAL_TASK_POLICY,
  DEFAULT_PRIVACY_POLICY,
  DEFAULT_RETENTION_POLICY,
  DEFAULT_THEME,
  DEFAULT_UPLOAD_POLICY,
  SETTING_KEYS,
  buildScopeChain,
  buildScopedSettingKey,
  resolveDefaultRecurrence,
  resolveSetting,
  type ApprovalPolicy,
  type CompletedTaskRetentionPolicy,
  type MediaHubRetentionPolicy,
  type NotificationPolicy,
  type OperationalFeaturesConfig,
  type PersonalTaskPolicy,
  type PrivacyPolicy,
  type RetentionPolicy,
  type TemplateChecklistStepDef,
  type UploadPolicy,
} from '@/domain/settings';
import type { RecurrenceRule } from '@/domain/types';
import type { Actor } from './org-service';

export async function loadAllSettings(gateway: DataGateway): Promise<SettingRecord[]> {
  return gateway.table('settings').list();
}

export interface ResolvedCompanySettings {
  approval: ApprovalPolicy;
  personal: PersonalTaskPolicy;
  notifications: NotificationPolicy;
  retention: RetentionPolicy;
  upload: UploadPolicy;
  privacy: PrivacyPolicy;
  theme: ThemeTokens;
  recurrence: RecurrenceRule;
  autoGenerateRecurring: boolean;
  completedTaskRetention: CompletedTaskRetentionPolicy;
  operationalFeatures: OperationalFeaturesConfig;
  mediaHubPolicy: MediaHubRetentionPolicy;
  templateChecklists: Record<string, TemplateChecklistStepDef[]>;
  timeZone: string;
  locale: string;
}

export function resolveCompanySettings(
  records: readonly SettingRecord[],
  companyId: string,
  scope: { brandId?: string | null; branchId?: string | null; userId?: string | null } = {},
): ResolvedCompanySettings {
  const chain = buildScopeChain({ companyId, ...scope });
  const orgChain = chain.filter((entry) => entry.scopeType !== 'user');
  return {
    approval: resolveSetting(records, chain, SETTING_KEYS.approvalPolicy, DEFAULT_APPROVAL_POLICY),
    personal: resolveSetting(records, chain, SETTING_KEYS.personalTaskPolicy, DEFAULT_PERSONAL_TASK_POLICY),
    notifications: resolveSetting(records, chain, SETTING_KEYS.notificationPolicy, DEFAULT_NOTIFICATION_POLICY),
    retention: resolveSetting(records, chain, SETTING_KEYS.retentionPolicy, DEFAULT_RETENTION_POLICY),
    upload: resolveSetting(records, chain, SETTING_KEYS.uploadPolicy, DEFAULT_UPLOAD_POLICY),
    privacy: resolveSetting(records, chain, SETTING_KEYS.privacyPolicy, DEFAULT_PRIVACY_POLICY),
    theme: resolveSetting(records, chain, SETTING_KEYS.theme, DEFAULT_THEME),
    recurrence: resolveDefaultRecurrence(records, chain),
    autoGenerateRecurring: resolveSetting(
      records,
      orgChain,
      SETTING_KEYS.autoGenerateRecurring,
      false,
    ),
    completedTaskRetention: {
      ...DEFAULT_COMPLETED_TASK_RETENTION,
      ...resolveSetting<Partial<CompletedTaskRetentionPolicy>>(
        records,
        orgChain,
        SETTING_KEYS.completedTaskRetention,
        DEFAULT_COMPLETED_TASK_RETENTION,
      ),
    },
    operationalFeatures: {
      ...DEFAULT_OPERATIONAL_FEATURES,
      ...resolveSetting<Partial<OperationalFeaturesConfig>>(
        records,
        orgChain,
        SETTING_KEYS.operationalFeatures,
        DEFAULT_OPERATIONAL_FEATURES,
      ),
    },
    mediaHubPolicy: {
      ...DEFAULT_MEDIA_HUB_POLICY,
      ...resolveSetting<Partial<MediaHubRetentionPolicy>>(
        records,
        orgChain,
        SETTING_KEYS.mediaHubPolicy,
        DEFAULT_MEDIA_HUB_POLICY,
      ),
    },
    templateChecklists: resolveSetting<Record<string, TemplateChecklistStepDef[]>>(
      records,
      orgChain,
      SETTING_KEYS.templateChecklists,
      {},
    ),
    timeZone: resolveSetting(records, chain, SETTING_KEYS.timeZone, 'Asia/Riyadh'),
    locale: resolveSetting(records, chain, SETTING_KEYS.locale, 'ar'),
  };
}

export async function saveSetting(
  gateway: DataGateway,
  actor: Actor,
  input: {
    scopeType: SettingRecord['scopeType'];
    scopeId: string;
    key: string;
    value: unknown;
  },
): Promise<SettingRecord> {
  if (input.scopeType === 'platform') {
    throw AppError.forbidden('إعدادات المنصة تُدار من مسار مالك المنصة المخصص');
  }
  if (input.scopeType === 'company' && input.scopeId !== actor.companyId) {
    throw AppError.forbidden('لا يمكن تعديل إعدادات منشأة أخرى');
  }

  // عند حفظ إعداد على مستوى براند أو فرع، نخزّنه داخل نطاق المنشأة بمفتاح مخصص
  // لضمان التوافق التام مع سياسات RLS على الخادم دون الحاجة لتعديل قاعدة البيانات.
  const effectiveScopeType: SettingRecord['scopeType'] =
    input.scopeType === 'brand' || input.scopeType === 'branch' ? 'company' : input.scopeType;
  const effectiveScopeId =
    input.scopeType === 'brand' || input.scopeType === 'branch' ? actor.companyId : input.scopeId;
  const effectiveKey =
    input.scopeType === 'brand' || input.scopeType === 'branch'
      ? buildScopedSettingKey(input.key, input.scopeType, input.scopeId)
      : input.key;

  const existing = await gateway.table('settings').list({
    filter: {
      scopeType: { op: 'eq', value: effectiveScopeType },
      scopeId: { op: 'eq', value: effectiveScopeId },
      key: { op: 'eq', value: effectiveKey },
    },
  });
  const now = new Date().toISOString();
  const saved =
    existing.length > 0
      ? await gateway.table('settings').update(existing[0]!.id, {
          value: input.value,
          updatedBy: actor.userId,
          updatedAt: now,
        })
      : await gateway.table('settings').insert({
          id: newId(),
          scopeType: effectiveScopeType,
          scopeId: effectiveScopeId,
          key: effectiveKey,
          value: input.value,
          updatedBy: actor.userId,
          updatedAt: now,
        });
  await gateway.recordAudit({
    companyId: actor.companyId,
    action: input.key === SETTING_KEYS.theme ? AUDIT_ACTIONS.themeUpdated : AUDIT_ACTIONS.settingsUpdated,
    entityType: 'setting',
    entityId: saved.id,
    before: existing[0]?.value ?? null,
    after: { scopeType: input.scopeType, scopeId: input.scopeId, key: input.key, value: input.value },
  });
  return saved;
}

export async function listModules(gateway: DataGateway, companyId: string): Promise<CompanyModule[]> {
  return gateway.table('company_modules').list({ filter: { companyId: { op: 'eq', value: companyId } } });
}

export async function toggleModule(
  gateway: DataGateway,
  actor: Actor,
  companyId: string,
  moduleKey: ModuleKey,
  enabled: boolean,
): Promise<CompanyModule> {
  const existing = await gateway.table('company_modules').list({
    filter: { companyId: { op: 'eq', value: companyId }, moduleKey: { op: 'eq', value: moduleKey } },
  });
  const now = new Date().toISOString();
  const row =
    existing.length > 0
      ? await gateway.table('company_modules').update(existing[0]!.id, {
          enabled,
          enabledAt: enabled ? now : null,
          enabledBy: enabled ? actor.userId : null,
        })
      : await gateway.table('company_modules').insert({
          id: newId(),
          companyId,
          moduleKey,
          enabled,
          settings: {},
          enabledAt: enabled ? now : null,
          enabledBy: enabled ? actor.userId : null,
        });
  await gateway.recordAudit({
    companyId,
    action: AUDIT_ACTIONS.companyModuleToggled,
    entityType: 'company_module',
    entityId: row.id,
    after: { moduleKey, enabled },
  });
  return row;
}

/** إعدادات جهاز محلية لا تُرفع للخادم (تفضيلات واجهة). */
const DEVICE_PREFIX = 'taskflow.device.';

export function getDeviceSetting<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(`${DEVICE_PREFIX}${key}`);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

export function setDeviceSetting(key: string, value: unknown): void {
  try {
    localStorage.setItem(`${DEVICE_PREFIX}${key}`, JSON.stringify(value));
  } catch {
    // تجاهل
  }
}
